// src/data/questions/profileQuestions.ts
// Organization Profile Questions (Q1-Q4)

import { Question } from '../../types';

export const profileQuestions: Question[] = [
  {
    id: 'Q1',
    section: 'profile',
    question: 'What type of organization are you?',
    type: 'single',
    options: [
      { value: 'provider', label: 'Covered Entity - Healthcare Provider', description: 'Hospital, clinic, physician practice', points: 0 },
      { value: 'plan', label: 'Covered Entity - Health Plan', description: 'Insurance, HMO, health plan', points: 0 },
      { value: 'clearinghouse', label: 'Covered Entity - Healthcare Clearinghouse', description: 'Claims processing, billing services', points: 0 },
      { value: 'ba', label: 'Business Associate', description: 'Vendor serving covered entities', points: 0 },
      { value: 'subcontractor', label: 'Subcontractor to Business Associate', description: 'Vendor to another BA', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: 'Unsure about organization type', points: 0, isUnknown: true },
    ],
    regulation: 'HIPAA Definitions',
    required: true,
    weight: 1,
    tooltipContent: {
      plainEnglish: 'This question determines your foundational HIPAA obligations. HIPAA regulations apply differently depending on whether you\'re a "Covered Entity" (provider, health plan, or clearinghouse) or a "Business Associate" (vendor serving covered entities). Your organization type dictates which rules apply, how strictly they apply, and what compliance responsibilities you must assume.',
      hipaaReference: '§164.103 (Definitions of Covered Entity, Business Associate, Subcontractor)',
      realWorldExample: 'A telemedicine startup thinks it\'s a Business Associate because it "serves" patients, but it actually operates as a Covered Entity (healthcare provider) because it provides healthcare services. It would be subject to full HIPAA Privacy, Security, and Breach Notification Rules—a much heavier burden than expected.',
      estimatedCost: {
        small: 'Free - classification determination',
        medium: 'Free - classification determination',
        large: 'Free - classification determination'
      },
      implementationSteps: [
        'Review your organization\'s legal structure and function',
        'Determine if you provide healthcare services directly (Covered Entity)',
        'Determine if you handle PHI on behalf of Covered Entities (Business Associate)',
        'Document your classification for compliance purposes'
      ],
      commonMistakes: [
        'Misclassifying organization type can result in total non-compliance',
        'Assuming all healthcare technology companies are Business Associates',
        'Not updating classification when business model changes'
      ],
      resourceLinks: [
        { title: "HHS Covered Entity Guidance", url: "https://www.hhs.gov/hipaa/for-professionals/covered-entities/index.html", type: "external" },
        { title: "HHS Business Associate Guidance", url: "https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/business-associates/index.html", type: "external" }
      ],
      timeToComplete: {
        small: 'Immediate - 1-2 business days',
        medium: 'Immediate - 1-2 business days',
        large: 'Immediate - 1-2 business days'
      }
    },
    investigationGuide: {
      whoToAsk: ['Legal counsel', 'Compliance officer', 'CEO/founder', 'Healthcare attorney'],
      whereToLook: ['Business license', 'Service agreements with clients', 'Revenue sources', 'Organizational chart'],
      howToVerify: 'Review whether you provide healthcare services directly to patients (Covered Entity) or handle PHI on behalf of other healthcare organizations (Business Associate)',
      whatToDocument: 'Document your entity classification and the reasoning behind it for compliance records'
    }
  },
  {
    id: 'Q2',
    section: 'profile',
    question: 'What is your organization size?',
    type: 'single',
    options: [
      { value: 'small', label: 'Small (1-50 employees)', description: '', points: 0 },
      { value: 'medium', label: 'Medium (51-500 employees)', description: '', points: 0 },
      { value: 'large', label: 'Large (500+ employees)', description: '', points: 0 },
      { value: 'unknown', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: 'Organization Profile',
    required: true,
    weight: 1,
    tooltipContent: {
      plainEnglish: 'Organization size impacts how you allocate resources to HIPAA compliance, scale your security infrastructure, and structure your governance. A 5-person clinic faces fundamentally different challenges than a 5,000-person health system. Smaller organizations often use simplified but still-compliant approaches; larger organizations must implement enterprise-grade security infrastructure and governance models.',
      hipaaReference: '§164.306 (Administrative Safeguards) - no specific size requirements; applies to all',
      realWorldExample: 'A 12-person family medicine clinic implements a cloud-based EHR (Kareo) with built-in HIPAA features, uses Google Workspace with a BAA for email/documents, adds MFA via Google, encrypts a shared laptop with BitLocker, and trains staff annually. Total cost: ~$500/month, 4 hours setup, 2 hours/year training.',
      estimatedCost: {
        small: '$500-2,000/month for compliant cloud tools',
        medium: '$2,000-8,000/month for EHR, identity, compliance tools',
        large: '$50,000-2,000,000+/year for enterprise infrastructure'
      },
      implementationSteps: [
        'Assess current employee count and growth projections',
        'Determine appropriate compliance tools for your size',
        'Plan phased implementation over 6-12 months',
        'Budget for size-appropriate security infrastructure'
      ],
      commonMistakes: [
        'Small organizations assuming compliance is too expensive',
        'Large organizations over-investing in redundant controls',
        'Not scaling compliance program as organization grows'
      ],
      resourceLinks: [
        { title: "HHS Security Rule Overview", url: "https://www.hhs.gov/hipaa/for-professionals/security/index.html", type: "external" }
      ],
      timeToComplete: {
        small: 'Immediate classification; 6-12 months for full implementation',
        medium: 'Immediate classification; 6-12 months for full implementation',
        large: 'Immediate classification; 6-12 months for full implementation'
      }
    },
    investigationGuide: {
      whoToAsk: ['HR manager', 'Operations manager', 'Finance team', 'IT director'],
      whereToLook: ['Employee roster', 'Payroll records', 'Organizational chart', 'Growth projections'],
      howToVerify: 'Count all employees, contractors, and volunteers who have access to PHI or systems containing PHI',
      whatToDocument: 'Document current headcount and projected growth to inform compliance planning and budgeting'
    }
  },
  {
    id: 'Q3',
    section: 'profile',
    question: 'Do you have a physical healthcare facility?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes, we have physical clinic/hospital facilities', description: '', points: 0 },
      { value: 'no', label: 'No, we are fully digital/cloud-based', description: '', points: 0 },
      { value: 'hybrid', label: 'Hybrid (some physical, some digital)', description: '', points: 0 },
      { value: 'unknown', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: 'Physical Safeguards Applicability',
    required: true,
    weight: 1,
    tooltipContent: {
      plainEnglish: 'This determines whether you must comply with the HIPAA Physical Safeguards Rule. Physical safeguards are the concrete, non-technical security measures that protect facilities, devices, and data storage. If you\'re fully cloud-based and don\'t have physical infrastructure, these rules don\'t apply to you. If you have any physical facility—even a small clinic with a server or a hybrid model with some on-premise systems—Physical Safeguards apply.',
      hipaaReference: '§164.310 - Physical Safeguards (Facility Access Controls, Workstation Use, Workstation Security, Device/Media Controls)',
      realWorldExample: 'A 40-person clinic with one server room implements a simple badge system ($5k), visitor log procedure, locked doors, workstation privacy screens, and contracts with an e-waste company for certified device destruction ($50/device). A telehealth startup operates fully in AWS with HIPAA-compliant Workspace; no physical facility, no Physical Safeguards needed.',
      estimatedCost: {
        small: '$5,000-30,000 one-time + $500-1,500/month',
        medium: '$15,000-75,000 one-time + $1,500-5,000/month',
        large: '$50,000-250,000 one-time + $5,000-20,000/month'
      },
      implementationSteps: [
        'Assess physical facility security needs',
        'Implement facility access controls (badge readers, locks)',
        'Deploy workstation security (privacy screens, auto-lock)',
        'Establish device disposal procedures with certified vendors'
      ],
      commonMistakes: [
        'Assuming cloud-only means no physical security obligations',
        'Not tracking device inventory and disposal',
        'Allowing unescorted visitor access to areas with PHI'
      ],
      resourceLinks: [
        { title: "HHS Physical Safeguards", url: "https://www.hhs.gov/hipaa/for-professionals/security/laws-regulations/index.html", type: "external" }
      ],
      timeToComplete: {
        small: '2-4 weeks',
        medium: '4-8 weeks',
        large: '8-16 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['Facilities manager', 'IT director', 'Security officer', 'Operations manager'],
      whereToLook: ['Office lease agreements', 'Server room locations', 'Device inventory', 'Cloud service agreements'],
      howToVerify: 'Determine if you have any physical locations where PHI is stored, processed, or accessed',
      whatToDocument: 'Document all physical locations, device inventory, and whether you use cloud-only or hybrid infrastructure'
    }
  },
  {
    id: 'Q4',
    section: 'profile',
    question: 'Which of these apply to your organization? (Select all that apply)',
    type: 'multiple',
    options: [
      { value: 'ai', label: 'We use AI/ML systems in clinical workflows', description: '', points: 0 },
      { value: 'cloud', label: 'We use cloud infrastructure (AWS, Azure, GCP)', description: '', points: 0 },
      { value: 'mobile', label: 'We have mobile applications', description: '', points: 0 },
      { value: 'multistate', label: 'We operate in multiple states', description: '', points: 0 },
      { value: 'international', label: 'We serve patients internationally', description: '', points: 0 },
      { value: 'genomic', label: 'We store genomic/biometric data', description: '', points: 0 },
      { value: 'none', label: 'None of the above', description: '', points: 0 },
      { value: 'unknown', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: 'Technology Profile',
    required: true,
    weight: 1,
    tooltipContent: {
      plainEnglish: 'This question identifies emerging technologies and operational models that create additional HIPAA complexity. Cloud infrastructure, AI/ML systems, mobile apps, multi-state operations, international patient services, and genomic data all introduce specific security and privacy considerations beyond baseline HIPAA. Each technology creates a new attack surface, regulatory obligation, or data residency requirement.',
      hipaaReference: '§164.308(a)(1)(ii) (Risk Analysis) requires consideration of technology profile',
      realWorldExample: 'A psychiatric clinic in California uses Slack for team communication (requires BAA), Zoom for telehealth (BAA), AWS for patient records (BAA with specific encryption requirements), and an AI tool for medication interaction checking (requires vendor BAA + audit logging). If they expand to Colorado and New York, they must comply with those states\' AI transparency laws.',
      estimatedCost: {
        small: '$5,000-30,000 setup; $500-5,000/month',
        medium: '$10,000-50,000 setup; $2,000-10,000/month',
        large: '$25,000-100,000 setup; $5,000-20,000/month'
      },
      implementationSteps: [
        'Identify all technologies and operational models in use',
        'Assess HIPAA requirements for each technology',
        'Negotiate Business Associate Agreements with vendors',
        'Implement technology-specific security controls'
      ],
      commonMistakes: [
        'Using cloud/AI without Business Associate Agreements',
        'Not complying with state-specific AI transparency laws',
        'Assuming HIPAA is sufficient for international operations'
      ],
      resourceLinks: [
        { title: "HHS Cloud Computing Guidance", url: "https://www.hhs.gov/hipaa/for-professionals/special-topics/cloud-computing/index.html", type: "external" },
        { title: "AWS HIPAA Compliance", url: "https://aws.amazon.com/compliance/hipaa-compliance/", type: "external" }
      ],
      timeToComplete: {
        small: '4-8 weeks',
        medium: '8-16 weeks',
        large: '12-24 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['CTO', 'IT director', 'Security officer', 'Compliance officer'],
      whereToLook: ['Technology stack documentation', 'Vendor contracts', 'Cloud service agreements', 'State licenses'],
      howToVerify: 'Review all systems, applications, and services that handle PHI to identify technology categories',
      whatToDocument: 'Document complete technology inventory with vendor names, BAA status, and compliance requirements'
    }
  },
];
